import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Badge, Button, Card, CardBody, Col, Input, Label, Row } from 'reactstrap';
import { useAuth } from '../../auth';
import Breadcrumb from '../../components/common/breadcrumb';
import { getAllWarehouses } from '../../services/apis';
import { addWorkshopItemFile, createWorkshopItem, fixWorkshopItem, getOpenCustodyDamage, getWorkshopVehicles, openWorkshopFile } from './api';
import { getMasterData } from '../devir-teslim/api';

const ROLE_GROUP = 42;

// Operasyon İzleme > Atölye: cars currently sitting in a TYPE_WORKSHOP zone of the selected depo. A purely additive
// enrichment layer (db-migrations/054_workshop_module.sql) — items here never change a custody_management record and
// "tamamlandı" never moves the car's zone; entering/leaving the workshop zone itself is done the usual way (Yerinde
// Adresleme scan or a manual move), not from this screen.
const emptyNewItem = (chassisNo) => ({ chassis_no: chassisNo, ref_damage_line_id: 0, component_code: '', damage_code: '', zone_code: '', note: '' });

const ItemRow = ({ item, canWrite, onFixed }) => {
    const [photo, setPhoto] = useState(null);
    const [doc, setDoc] = useState(null);
    const [busy, setBusy] = useState(false);
    const [fixNote, setFixNote] = useState('');

    const upload = async (kind, file, clear) => {
        setBusy(true);
        try {
            await addWorkshopItemFile(item.id, kind, file);
            toast.success('Yüklendi');
            clear(null);
            onFixed();
        } catch (e) {
            toast.error(e?.response?.data?.error || 'Yüklenemedi');
        } finally {
            setBusy(false);
        }
    };

    const fix = async () => {
        if (item.source === 'CUSTODY' && !fixNote.trim()) {
            toast.error('Devir-teslim hasarını kapatmak için tamamlama notu girilmeli');
            return;
        }
        setBusy(true);
        try {
            await fixWorkshopItem(item.id, fixNote.trim());
            toast.success(item.source === 'CUSTODY' ? 'Tamamlandı ve devir-teslim hasarı kapatıldı' : 'Tamamlandı olarak işaretlendi');
            onFixed();
        } catch (e) {
            toast.error(e?.response?.data?.error || 'İşaretlenemedi');
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="border rounded p-2 mb-2">
            <Row>
                <Col md="6">
                    <b>{item.zone_name} · {item.component_name} · {item.damage_name}</b>{' '}
                    <Badge color={item.source === 'CUSTODY' ? 'info' : 'secondary'}>{item.source === 'CUSTODY' ? 'devir-teslimden' : 'elle eklendi'}</Badge>{' '}
                    <Badge color={item.status === 'TAMAMLANDI' ? 'success' : 'warning'}>{item.status === 'TAMAMLANDI' ? 'tamamlandı' : 'açık'}</Badge>
                    {item.ref_handover_no && <span className="text-muted ml-1">({item.ref_handover_no})</span>}
                    {item.note && <div className="text-muted">{item.note}</div>}
                    {item.fix_note && <div className="text-success">Onarım notu: {item.fix_note}</div>}
                    <div className="text-muted small">Ekleyen: {item.created_by_name}{item.fixed_by_name ? ` · Tamamlayan: ${item.fixed_by_name}` : ''}</div>
                </Col>
                <Col md="6">
                    <div className="mb-1">
                        {item.files.map((f) => (
                            <Button key={f.id} size="sm" color="link" className="mr-2 p-0" onClick={() => openWorkshopFile(f.path)}>
                                {f.kind === 'PHOTO' ? '📷' : '📄'} {f.filename}
                            </Button>
                        ))}
                    </div>
                    {canWrite && item.status === 'ACIK' && (
                        <Row>
                            <Col md="5">
                                <Input bsSize="sm" type="file" accept="image/*" disabled={busy}
                                    onChange={(e) => e.target.files[0] && upload('PHOTO', e.target.files[0], setPhoto)} />
                            </Col>
                            <Col md="5">
                                <Input bsSize="sm" type="file" accept="application/pdf,image/*" disabled={busy}
                                    onChange={(e) => e.target.files[0] && upload('DOCUMENT', e.target.files[0], setDoc)} />
                            </Col>
                            <Col md="10" className="mt-1">
                                <Input bsSize="sm" value={fixNote} disabled={busy}
                                    placeholder={item.source === 'CUSTODY' ? 'Onarım notu (zorunlu — devir-teslim hasarını kapatır)' : 'Onarım notu (isteğe bağlı)'}
                                    onChange={(e) => setFixNote(e.target.value)} />
                            </Col>
                            <Col md="2" className="mt-1">
                                <Button size="sm" color="success" disabled={busy} onClick={fix}>Tamamlandı</Button>
                            </Col>
                        </Row>
                    )}
                </Col>
            </Row>
        </div>
    );
};

const AddItemForm = ({ chassisNo, md, onAdded, onCancel }) => {
    const [openDamage, setOpenDamage] = useState([]);
    const [item, setItem] = useState(emptyNewItem(chassisNo));
    const [busy, setBusy] = useState(false);

    useEffect(() => { getOpenCustodyDamage(chassisNo).then(setOpenDamage).catch(() => setOpenDamage([])); }, [chassisNo]);

    const pickRef = (id) => {
        const d = openDamage.find((x) => x.damage_line_id === Number(id));
        setItem({ ...item, ref_damage_line_id: Number(id), component_code: d?.component_code || '', damage_code: d?.damage_code || '',
            zone_code: d?.zone_code || '', note: d?.note || '' });
    };

    const save = async () => {
        if (!item.component_code || !item.damage_code || !item.zone_code) {
            toast.error('Bölge, parça ve hasar kodu seçilmeli');
            return;
        }
        setBusy(true);
        try {
            await createWorkshopItem(item);
            toast.success('Onarım kalemi eklendi');
            onAdded();
        } catch (e) {
            toast.error(e?.response?.data?.error || 'Eklenemedi');
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="border rounded p-2 mb-2 bg-light">
            <Row>
                <Col md="5">
                    <Label className="mb-0">Devir-teslimdeki açık hasardan seç (isteğe bağlı)</Label>
                    <Input bsSize="sm" type="select" value={item.ref_damage_line_id} onChange={(e) => pickRef(e.target.value)}>
                        <option value={0}>— elle gir —</option>
                        {openDamage.map((d) => (
                            <option key={d.damage_line_id} value={d.damage_line_id}>
                                {d.handover_no} · {d.zone_name} · {d.component_name} · {d.damage_name}
                            </option>
                        ))}
                    </Input>
                </Col>
            </Row>
            <Row className="mt-2">
                <Col md="3"><Label className="mb-0">Bölge</Label>
                    <Input bsSize="sm" type="select" disabled={item.ref_damage_line_id > 0} value={item.zone_code}
                        onChange={(e) => setItem({ ...item, zone_code: e.target.value })}>
                        <option value="">seçiniz</option>
                        {(md.zones || []).filter((z) => !z.is_excluded).map((z) => <option key={z.code} value={z.code}>{z.name_tr}</option>)}
                    </Input></Col>
                <Col md="4"><Label className="mb-0">Parça</Label>
                    <Input bsSize="sm" type="select" disabled={item.ref_damage_line_id > 0} value={item.component_code}
                        onChange={(e) => setItem({ ...item, component_code: e.target.value })}>
                        <option value="">seçiniz</option>
                        {(md.components || []).filter((c) => c.is_active !== false).map((c) => <option key={c.code} value={c.code}>{c.code} · {c.name_tr}</option>)}
                    </Input></Col>
                <Col md="3"><Label className="mb-0">Hasar kodu</Label>
                    <Input bsSize="sm" type="select" disabled={item.ref_damage_line_id > 0} value={item.damage_code}
                        onChange={(e) => setItem({ ...item, damage_code: e.target.value })}>
                        <option value="">seçiniz</option>
                        {(md.damage_codes || []).filter((c) => c.is_active !== false).map((c) => <option key={c.code} value={c.code}>{c.code} · {c.name_tr}</option>)}
                    </Input></Col>
                <Col md="2" className="d-flex align-items-end">
                    <Button size="sm" color="primary" className="mr-1" disabled={busy} onClick={save}>Ekle</Button>
                    <Button size="sm" color="secondary" onClick={onCancel}>Vazgeç</Button>
                </Col>
            </Row>
            <Row className="mt-2">
                <Col md="12"><Input bsSize="sm" placeholder="not" value={item.note} onChange={(e) => setItem({ ...item, note: e.target.value })} /></Col>
            </Row>
        </div>
    );
};

const VehicleCard = ({ v, md, canWrite, onChanged }) => {
    const [adding, setAdding] = useState(false);
    return (
        <Card className="mb-2">
            <CardBody>
                <Row>
                    <Col md="8">
                        <h6 className="mb-0">{v.chassis_no} <span className="text-muted">{v.brand} {v.model}</span></h6>
                        <div className="text-muted small">{v.zone_name} · {v.address_code}{v.entered_at ? ` · ${new Date(v.entered_at).toLocaleString('tr-TR')}` : ''}</div>
                    </Col>
                    <Col md="4" className="text-right">
                        <Badge color={v.open_items > 0 ? 'warning' : 'success'}>{v.open_items} açık kalem</Badge>{' '}
                        {canWrite && <Button size="sm" color="primary" outline onClick={() => setAdding(!adding)}>{adding ? 'Vazgeç' : '+ Hasar ekle'}</Button>}
                    </Col>
                </Row>
                {adding && <AddItemForm chassisNo={v.chassis_no} md={md} onAdded={() => { setAdding(false); onChanged(); }} onCancel={() => setAdding(false)} />}
                <div className="mt-2">
                    {v.items.length === 0 && <div className="text-muted">Henüz onarım kalemi eklenmedi.</div>}
                    {v.items.map((it) => <ItemRow key={it.id} item={it} canWrite={canWrite} onFixed={onChanged} />)}
                </div>
            </CardBody>
        </Card>
    );
};

const Atolye = () => {
    const { hasRole } = useAuth(ROLE_GROUP);
    const canWrite = hasRole('atolye_write');
    const [warehouses, setWarehouses] = useState([]);
    const [warehouseId, setWarehouseId] = useState(
        localStorage.getItem("warehouse") ? parseInt(Buffer.from(localStorage.getItem("warehouse"), 'base64').toString('ascii')) : null
    );
    const [vehicles, setVehicles] = useState([]);
    const [md, setMd] = useState({});
    const [loading, setLoading] = useState(false);

    useEffect(() => { getAllWarehouses().then(setWarehouses).catch(() => setWarehouses([])); }, []);
    useEffect(() => { getMasterData().then(setMd).catch(() => setMd({})); }, []);

    const load = useCallback(() => {
        if (!warehouseId) return;
        setLoading(true);
        getWorkshopVehicles(warehouseId).then(setVehicles).catch((e) => {
            toast.error(e?.response?.data?.error || 'Araçlar alınamadı');
            setVehicles([]);
        }).finally(() => setLoading(false));
    }, [warehouseId]);

    useEffect(() => { load(); }, [load]);

    return (
        <>
            <Breadcrumb parent="Operasyon İzleme" current="Atölye" />
            <Card><CardBody>
                <div className="text-muted mb-2">
                    Atölye (TYPE_WORKSHOP) bölgesindeki araçlar ve onarım kalemleri. Bu ekran devir-teslim kaydını hiç değiştirmez —
                    bir kalemin "tamamlandı" işaretlenmesi yalnızca burada izlenir; aracın zone girişi/çıkışı Yerinde Adresleme veya
                    manuel taşıma ile yapılır, bu ekrandan yapılmaz.
                </div>
                <Row className="mb-3">
                    <Col md="4">
                        <Label>Depo</Label>
                        <Input type="select" value={warehouseId || ''} onChange={(e) => setWarehouseId(Number(e.target.value) || null)}>
                            <option value="">— depo seçiniz —</option>
                            {warehouses.map((w) => <option key={w.id} value={w.id}>{w.warehouse_name}</option>)}
                        </Input>
                    </Col>
                </Row>
                {loading && <div>Yükleniyor…</div>}
                {!loading && warehouseId && vehicles.length === 0 && <div className="text-muted">Bu depoda atölyede araç yok.</div>}
                {vehicles.map((v) => <VehicleCard key={v.chassis_no} v={v} md={md} canWrite={canWrite} onChanged={load} />)}
            </CardBody></Card>
        </>
    );
};

export default Atolye;
